import React, { useMemo, useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { MODELS } from "../../mock/mockData";

const FILTERS = {
  model: ["Todos", "Power", "Nice", "Briza"],
  voltage: ["Todas", "48V", "60V"],
};

const Catalog = () => {
  const [model, setModel] = useState("Todos");
  const [voltage, setVoltage] = useState("Todas");

  const list = useMemo(() => {
    return MODELS.filter((m) => {
      const okModel = model === "Todos" || m.name === model;
      const okVolt = voltage === "Todas" || m.voltages.includes(voltage);
      return okModel && okVolt;
    });
  }, [model, voltage]);

  return (
    <section id="modelos" className="border-t-[3px] border-black">
      <div className="max-w-[1440px] mx-auto px-5 md:px-10 py-16 md:py-24">
        <div className="mb-10">
          <div className="flex items-center gap-3 mb-5">
            <span className="mono text-[11px] uppercase tracking-widest px-3 py-1 brut-border bg-white">03</span>
            <span className="mono text-[11px] uppercase tracking-widest text-black/60">Catálogo</span>
            <div className="flex-1 h-[3px] bg-black" />
          </div>
          <h2 className="display font-bold text-[36px] sm:text-[56px] md:text-[80px] lg:text-[96px] leading-[0.9]">
            Três modelos.
            <br />
            <span className="italic font-normal">Toda a gama</span> em lítio.
          </h2>
          <p className="mt-6 max-w-[700px] text-[16px] md:text-[18px] leading-[1.5]">
            Power, Nice e Briza — disponíveis em 48V e 60V, com baterias de 20Ah, 24Ah ou 30Ah. Mesma plataforma, posicionamentos distintos.
          </p>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-8">
          <div className="flex items-center gap-2">
            <span className="mono text-[11px] uppercase tracking-widest text-black/60 mr-1">Modelo:</span>
            {FILTERS.model.map((m) => (
              <button
                key={m}
                onClick={() => setModel(m)}
                className={`mono text-[12px] uppercase font-medium tracking-wider px-3 py-2 brut-border brut-btn ${
                  model === m ? "bg-black text-[#FFD60A]" : "bg-white"
                }`}
              >
                {m}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <span className="mono text-[11px] uppercase tracking-widest text-black/60 mr-1 ml-3">Voltagem:</span>
            {FILTERS.voltage.map((v) => (
              <button
                key={v}
                onClick={() => setVoltage(v)}
                className={`mono text-[12px] uppercase font-medium tracking-wider px-3 py-2 brut-border brut-btn ${
                  voltage === v ? "bg-[#E63946] text-white" : "bg-white"
                }`}
              >
                {v}
              </button>
            ))}
          </div>
        </div>

        {/* Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {list.map((m) => {
            const accentBg =
              m.accent === "red" ? "bg-[#E63946]" :
              m.accent === "yellow" ? "bg-[#FFD60A]" :
              "bg-black";
            const accentTxt = m.accent === "yellow" ? "text-black" : "text-white";
            return (
              <article key={m.id} className="brut-border bg-white brut-card flex flex-col">
                <div className="flex items-center justify-between px-5 py-3 border-b-[3px] border-black bg-[#F4F0E8]">
                  <span className="mono text-[11px] uppercase tracking-widest font-bold">
                    {m.name} · SKU {m.sku}
                  </span>
                  <span className={`mono text-[11px] uppercase tracking-widest font-bold px-2 py-1 ${accentBg} ${accentTxt}`}>
                    100% LÍTIO
                  </span>
                </div>

                <div className="relative aspect-[4/3] overflow-hidden bg-[#F4F0E8] border-b-[3px] border-black">
                  <img
                    src={m.image}
                    alt={`Scooter electrica Veentto ${m.name} para revendedores B2B`}
                    width="640"
                    height="480"
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-contain p-4"
                  />
                  <span className="absolute top-3 left-3 mono text-[10px] uppercase tracking-widest font-bold bg-black text-[#FFD60A] px-2 py-1">
                    {m.voltages.join(" / ")}
                  </span>
                  <span className="absolute bottom-3 right-3 mono text-[10px] uppercase tracking-widest font-bold bg-white px-2 py-1 brut-border">
                    {m.capacities.join(" · ")}
                  </span>
                </div>

                <div className="p-6 flex flex-col flex-1">
                  <h3 className="display text-[40px] md:text-[48px] font-bold leading-none">{m.name}</h3>
                  <div className="mono text-[12px] uppercase text-[#E63946] font-bold mt-1">{m.tagline}</div>
                  <p className="mt-3 text-[14px] leading-[1.5] text-black/80">{m.description}</p>

                  <div className="mt-5 grid grid-cols-2 gap-0 border-[3px] border-black">
                    <div className="p-3 border-r-[3px] border-black">
                      <div className="mono text-[10px] uppercase tracking-widest text-black/60">PVP desde</div>
                      <div className="display text-[22px] font-bold leading-none mt-1">{m.pvp}</div>
                    </div>
                    <div className="p-3 bg-[#FFD60A]">
                      <div className="mono text-[10px] uppercase tracking-widest text-black/70">Margem desde</div>
                      <div className="display text-[22px] font-bold leading-none mt-1">{m.margin}</div>
                    </div>
                    <div className="p-3 border-t-[3px] border-r-[3px] border-black">
                      <div className="mono text-[10px] uppercase tracking-widest text-black/60">Bateria</div>
                      <div className="text-[13px] font-bold mt-1">Lítio 48/60V</div>
                    </div>
                    <div className="p-3 border-t-[3px] border-black">
                      <div className="mono text-[10px] uppercase tracking-widest text-black/60">Garantia</div>
                      <div className="text-[13px] font-bold mt-1">1 ano bateria</div>
                    </div>
                  </div>

                  <a
                    href="#candidatura"
                    className={`mt-6 inline-flex items-center justify-between gap-2 ${accentBg} ${accentTxt} font-bold mono text-[12px] uppercase tracking-wider px-5 py-4 brut-border brut-btn`}
                  >
                    Pedir tabela {m.name}
                    <ArrowUpRight size={18} />
                  </a>
                </div>
              </article>
            );
          })}
        </div>

        {list.length === 0 && (
          <div className="text-center py-12 brut-border bg-white">
            <p className="mono uppercase text-sm">Nenhum modelo corresponde aos filtros.</p>
          </div>
        )}
      </div>
    </section>
  );
};

export default Catalog;
